import { useEffect, useRef } from "react";

/**
 * A footer (e.g. a composer) pinned over the bottom of a scroll list that
 * scrolls away with the content, headroom-style, one pixel at a time:
 *
 * - scrolling the list UP moves the footer down with the content until it is
 *   fully hidden; scrolling DOWN slides it back up by the same amount;
 * - at the very bottom of the list (within 1px), or when the list does not
 *   overflow, it is always fully shown;
 * - focus or typing inside the footer shows it again at once, so nobody
 *   types into a composer they cannot see.
 *
 * The footer must be `position: absolute; bottom: 0` inside a wrapper that
 * clips (`overflow: clip`, or `hidden` where clip is unsupported — clip also
 * stops a focus or caret reveal from scrolling the wrapper), and the list must
 * be that wrapper's scroll container. The list gets `padding-bottom` equal to
 * the footer's height (kept in sync with a ResizeObserver, since a growing
 * textarea changes it), so the last row is never hidden under the footer when
 * scrolled to the bottom, and `scroll-padding-bottom` equal to the footer's
 * visible part, so a focused row (an inline edit) is revealed above it. The
 * footer stops at the list's scrollbar instead of covering it. A list that
 * sits at the bottom stays there when its rows or the footer grow (a new row,
 * an inline edit, the pane being shown again after display:none).
 *
 * While the list is display:none (clientHeight 0) nothing is recomputed, so a
 * pane shown again keeps its scroll position and the footer's offset.
 *
 * Performance: one passive scroll listener, throttled to one update per
 * animation frame, and the offset is written straight to the footer's style —
 * never React state — so scrolling does not re-render the list.
 *
 * Attach `listRef` to the scroll list, `contentRef` (optional) to the element
 * wrapping its rows, and `footerRef` to the footer. Pass `footerEnabled =
 * false` while the footer is not rendered; the list still keeps to the bottom.
 */
export const useScrollAwayFooter = <
    L extends HTMLElement = HTMLDivElement,
    F extends HTMLElement = HTMLDivElement,
    C extends HTMLElement = HTMLDivElement
>(
    footerEnabled = true
) => {
    const listRef = useRef<L>(null);
    const footerRef = useRef<F>(null);
    const contentRef = useRef<C>(null);

    useEffect(() => {
        const list = listRef.current;
        if (!list) return undefined;
        const footer = footerEnabled ? footerRef.current : null;

        let footerHeight = 0;
        let offset = 0;
        let gutter = 0;
        let lastTop = list.scrollTop;
        let atBottom = list.scrollHeight - list.clientHeight - list.scrollTop <= 1;
        let frame: number | null = null;

        // The pane is display:none: every size reads 0, so there is nothing
        // to learn (and a 0-height list would look "at the bottom").
        const isHidden = () => list.clientHeight === 0;

        // Focus and scroll-into-view clear the footer's visible part.
        const syncScrollPadding = () => {
            const visible = Math.max(0, footerHeight - offset);
            list.style.scrollPaddingBottom = visible ? `${visible}px` : "";
        };

        const update = () => {
            frame = null;
            if (isHidden()) return;
            const max = Math.max(0, list.scrollHeight - list.clientHeight);
            // Clamp so iOS/macOS rubber-band overscroll (scrollTop < 0 or past
            // the end) never counts as a scroll.
            const top = Math.min(Math.max(list.scrollTop, 0), max);
            const delta = top - lastTop;
            lastTop = top;
            atBottom = max <= 0 || max - top <= 1;
            if (!footer) return;

            // Stop the footer at the list's scrollbar. Written here, in the
            // animation frame, not in a ResizeObserver callback: it resizes
            // the footer, and doing that inside an observer callback raises
            // "ResizeObserver loop completed with undelivered notifications".
            const nextGutter = list.offsetWidth - list.clientWidth;
            if (nextGutter !== gutter) {
                gutter = nextGutter;
                footer.style.right = gutter ? `${gutter}px` : "";
            }

            const next = atBottom ? 0 : Math.min(Math.max(offset - delta, 0), footerHeight);
            if (next === offset) return;
            offset = next;
            footer.style.transform = offset ? `translate3d(0, ${offset}px, 0)` : "";
            syncScrollPadding();
        };

        const schedule = () => {
            if (frame != null) return;
            frame = window.requestAnimationFrame(update);
        };

        // Rows, list box or footer changed size: keep a list that was at the
        // bottom on its (new) last row, then re-check the overflow and the
        // scrollbar width.
        const keepBottom = () => {
            if (isHidden()) return;
            if (atBottom) list.scrollTop = list.scrollHeight;
            schedule();
        };

        const syncFooterHeight = () => {
            if (isHidden()) return;
            footerHeight = footer ? footer.offsetHeight : 0;
            list.style.paddingBottom = footerHeight ? `${footerHeight}px` : "";
            syncScrollPadding();
            keepBottom();
        };
        syncFooterHeight();

        // Focus (Tab) or typing in a scrolled-away footer: show it in full.
        const reveal = () => {
            if (!footer || !offset) return;
            offset = 0;
            footer.style.transform = "";
            syncScrollPadding();
        };

        list.addEventListener("scroll", schedule, { passive: true });
        footer?.addEventListener("focusin", reveal);
        footer?.addEventListener("input", reveal);

        const hasResizeObserver = typeof ResizeObserver !== "undefined";
        const footerObserver = hasResizeObserver && footer ? new ResizeObserver(syncFooterHeight) : null;
        footerObserver?.observe(footer as Element);
        const contentObserver = hasResizeObserver ? new ResizeObserver(keepBottom) : null;
        // Border box: the padding this hook writes must not re-trigger it.
        contentObserver?.observe(list, { box: "border-box" });
        if (contentRef.current) contentObserver?.observe(contentRef.current);

        return () => {
            list.removeEventListener("scroll", schedule);
            footer?.removeEventListener("focusin", reveal);
            footer?.removeEventListener("input", reveal);
            footerObserver?.disconnect();
            contentObserver?.disconnect();
            if (frame != null) window.cancelAnimationFrame(frame);
            list.style.paddingBottom = "";
            list.style.scrollPaddingBottom = "";
            if (footer) {
                footer.style.transform = "";
                footer.style.right = "";
            }
        };
    }, [footerEnabled]);

    return { listRef, footerRef, contentRef };
};
